// Boshliq (2026-09-05): telefon uchun jadval o'rniga KARTALAR — antd `columns` + `dataSource` dan har qatorni karta qilib chizadi.
// Birinchi (nom) ustun — karta sarlavhasi; qolganlari «Yorliq: qiymat» grid; «Действия» ustuni — pastda to'liq kenglikda, katta tugmalar.
// Desktop'da ishlatilmaydi (ResponsiveTable / TableExtended ekran < md bo'lganda chaqiradi).
import React, { useMemo, useState } from "react"
import { Checkbox, Empty, Pagination, Radio, Spin } from "antd"
import "./MobileCards.less"

type Col = any

const isActionsCol = (c: Col) => {
  const key = String(c.key || c.dataIndex || "").toLowerCase()
  const title = typeof c.title === "string" ? c.title : ""
  return key === "actions" || key === "action" || /действ|amallar|actions?$/i.test(title)
}
const isNoCol = (c: Col) => String(c.title) === "№" || String(c.key || c.dataIndex || "") === "no"
// s250b (tog‘a #2065 S4 web sinovi): rasm ustuni (dataIndex mainProduct) karta sarlavhasi bo‘lib qolardi — tovar nomi ko‘rinmasdi
const isPhotoCol = (c: Col) => /фото|photo|rasm|изображ|image/i.test(typeof c.title === "string" ? c.title : "")
const flatCols = (cols: Col[]): Col[] => cols.flatMap((c) => (Array.isArray(c?.children) && c.children.length ? flatCols(c.children) : [c]))
const cellValue = (c: Col, rec: any, idx: number) => {
  const di = c.dataIndex
  // s250c (tog‘a #2065 S4 web sinovi): dataIndex bo‘lmasa antd kabi BUTUN QATOR beriladi (rc-table getPathValue) — avval undefined edi,
  // `render: (text) => <Amal data={text} />` ustunlari telefonda bo‘sh ma’lumot olib, tugmalar hech narsa qilmasdi
  const noPath = di === undefined || di === null || di === "" || (Array.isArray(di) && di.length === 0)
  const raw = noPath ? rec : Array.isArray(di) ? di.reduce((o: any, k: string) => (o == null ? o : o[k]), rec) : rec?.[di]
  const v = c.render ? c.render(raw, rec, idx) : raw
  // antd `render` {children, props} shaklini ham qo'llab-quvvatlash
  if (v && typeof v === "object" && !React.isValidElement(v) && "children" in v) return (v as any).children
  return v
}
// tanlash rejimida karta ichidagi tugma/havola/maydonga tegish tanlamaydi (ularning o'z amali bor)
const pickIgnore = (e: any) => {
  const t = e?.target as HTMLElement | undefined
  return !!(t && typeof t.closest === "function" && t.closest("a,button,input,textarea,.ant-select,.ant-picker,.ant-switch,.ant-dropdown-trigger") && !t.closest(".mobile-card-check"))
}
const isEmpty = (v: any) => v === null || v === undefined || v === "" || v === "—" || v === "-" || (Array.isArray(v) && v.length === 0)

export interface MobileCardsProps {
  columns: Col[]
  dataSource?: readonly any[]
  rowKey?: string | ((r: any) => string | number)
  loading?: boolean
  emptyText?: React.ReactNode
  pageSize?: number          // 0 = sahifalashsiz (server sahifalashi tashqarida)
  rowClassName?: (r: any, i: number) => string
  onRowClick?: (r: any, e?: any) => void   // tog'a #3410: hodisa ham beriladi — tugma/havolaga tegilsa karta ochilmaydi
  // Tanlash oynalari (ProductSelect va b., 2026-10-06 #3608 telefon sinovida topildi): kartada tanlash yo'q edi va bosilsa yozuv ochilardi.
  // Berilsa — kartaga bosish TANLAYDI (radio: bittasi, checkbox: qo'shadi/olib tashlaydi), chapda belgi, tanlangani ajralib turadi.
  selection?: { type: "radio" | "checkbox"; isSelected: (r: any) => boolean; onToggle: (r: any) => void }
}

export default function MobileCards({ columns, dataSource, rowKey = "id", loading, emptyText, pageSize = 20, rowClassName, onRowClick, selection }: MobileCardsProps) {
  const [page, setPage] = useState(1)
  const cols = useMemo(() => flatCols(columns || []).filter((c) => c && !isNoCol(c)), [columns])
  // sarlavha: nom/nomlanish ustuni (products «12», counterparties «60» — ID sarlavha bo'lib qolmasin); topilmasa — birinchi ustun
  const NAME_RX = /name|fullname|title|model|nomi|назван|имя|контрагент|counterparty|client|mijoz|product|tovar/i
  const titleCol =
    cols.find((c) => !isActionsCol(c) && !isPhotoCol(c) && NAME_RX.test(String(c.dataIndex || c.key || "") + " " + (typeof c.title === "string" ? c.title : ""))) ||
    cols.find((c) => !isActionsCol(c)) ||
    null
  const actionsCol = cols.find(isActionsCol) || null
  const fieldCols = cols.filter((c) => c !== titleCol && c !== actionsCol)
  const rows = (dataSource || []) as any[]
  const paged = pageSize > 0 ? rows.slice((page - 1) * pageSize, page * pageSize) : rows
  const keyOf = (r: any, i: number) => (typeof rowKey === "function" ? rowKey(r) : r?.[rowKey]) ?? i

  if (!loading && rows.length === 0) return <Empty description={emptyText || undefined} style={{ margin: "24px 0" }} />
  return (
    <Spin spinning={!!loading}>
      <div className="mobile-cards">
        {paged.map((r, i) => {
          const idx = (page - 1) * (pageSize || 0) + i
          const title = titleCol ? cellValue(titleCol, r, idx) : null
          const actions = actionsCol ? cellValue(actionsCol, r, idx) : null
          return (
            <div key={keyOf(r, idx)} data-testid={selection ? "mobile-pick-card" : undefined}
              className={`mobile-card${selection ? " mobile-card-pick" + (selection.isSelected(r) ? " mobile-card-picked" : "") : onRowClick ? " mobile-card-tap" : ""} ${rowClassName ? rowClassName(r, idx) : ""}`}
              onClick={selection ? (e) => { if (!pickIgnore(e)) selection.onToggle(r) } : onRowClick ? (e) => onRowClick(r, e) : undefined}>
              {selection && <span className="mobile-card-check" aria-hidden>{selection.type === "radio" ? <Radio checked={selection.isSelected(r)} /> : <Checkbox checked={selection.isSelected(r)} />}</span>}
              {!isEmpty(title) && <div className="mobile-card-title">{title}</div>}
              <div className="mobile-card-grid">
                {fieldCols.map((c, ci) => {
                  const v = cellValue(c, r, idx)
                  if (isEmpty(v)) return null
                  return (
                    <div key={String(c.key || c.dataIndex || ci)} className="mobile-card-field">
                      <span className="mobile-card-label">{typeof c.title === "function" ? c.title({}) : c.title}</span>
                      <span className="mobile-card-value">{v}</span>
                    </div>
                  )
                })}
              </div>
              {!isEmpty(actions) && <div className="mobile-card-actions" onClick={(e) => e.stopPropagation()}>{actions}</div>}
            </div>
          )
        })}
        {pageSize > 0 && rows.length > pageSize && (
          <Pagination size="small" current={page} pageSize={pageSize} total={rows.length} onChange={setPage} showSizeChanger={false} style={{ textAlign: "center", margin: "8px 0" }} />
        )}
      </div>
    </Spin>
  )
}
